import type {Component, Editor} from 'grapesjs';
import {getUniqueId} from '@Utils/string.util';
import type {ImageComponentData, TextComponentData} from '@Libraries/email-campaigns-library';
import {getEmailTextWithCcid, isMobileWidth} from '@Libraries/email-campaigns-library';
import type {EmailCampaignAiRedesignSource, EmailCampaignAiRewriteSource} from '@Libraries/ai-email-campaign-content-library';
import {EmailCampaignAiRegenerateImageSource} from '@Libraries/ai-email-campaign-content-library';
import {
  gatherImagesData,
  getEmailContextForImageGeneration,
  initiateAiRedesignImageRegeneration,
  pollAndReplaceGeneratedImages,
  settleEmailCampaignAiRedesignImageRegeneration,
} from '@Libraries/email-campaign-ai-image-regeneration-library';
import {applyAiRedesignChanges, handleAiRedesignError, handleAiRedesignFeedback, saveAiRedesignGenerationResults} from '@Libraries/email-campaign-ai-redesign-library';
import {fetchAiRewriteRecentPrompt} from '@Libraries/email-campaign-ai-rewrite-library';
import {AiTextPromptBoxError} from '@Libraries/ai-text-prompt-box-library';
import type {ColorPaletteInfo, ColorPalettePill} from '@Libraries/color-palettes.library';
import {ColorPaletteType} from '@Libraries/color-palettes.library';
import type {ErrorType, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiTextErrorTypes} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {EditableComponentsMap, SelectedPromptOptionValues} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {AiModel, PromptOption, RedesignMood} from '@Components/ai-redesign-prompt-box/ai-redesign-prompt-box.types';
import {setIsLoading, setLastRunCreditsCharged} from '@Components/email-maker-ai-redesign-prompt-box/email-maker-ai-redesign-prompt-box-reducer';
import type {ApplyColorPaletteToEmailServerResponse} from '@Components/auto-branding/auto-branding.types';
import {getTeamCredits, refreshTeamCreditsInStore} from '@Components/team-credits/team-credits-library';
import {initiatePosterMorphLoading, terminatePosterMorphLoading} from '@PosterWhiteboard/poster/poster-init';
import type {PosterPageHashedIdInfo} from '@Libraries/poster-library';
import {EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS, EMAIL_OVERLAY_ANIMATION_TIME_MS} from '@EmailMakerReact/email-maker-react.constants';
import {EMAIL_MAKER_AI_REDESIGN_LOADING_TOAST, EmailOverlayStyle} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {getAllComponentsByType, getStringProp} from '@EmailMakerReact/lib/email-maker-component-query-library';
import {toggleComponentLoadingOverlay, toggleLoadingOverlayForEmail} from '@EmailMakerReact/lib/email-maker-overlay-library';
import {replaceHtmlContent} from '@EmailMakerReact/lib/email-maker-html-export-library';
import {unselectComponent} from '@EmailMakerReact/lib/email-maker-selection-library';
import {setImageLoading} from '@EmailMakerReact/lib/email-maker-media-library';
import {loadDesignForId, loadDesignPageForId} from '@EmailMakerReact/lib/email-maker-design-preview-library';
import {logEmailMakerError} from '@EmailMakerReact/lib/email-maker-error-library';

const AI_EDIT_STATE_MODEL_KEY = 'pmwEmailMakerAiEditState';
const AI_EDIT_HIGHLIGHT_CLASS = '-highlighted-for-ai-edit';
const GJS_DASHED_SELECTOR = '.gjs-dashed';
const AI_EDIT_FRAME_CLASS = 'ai-edit';
/** Delay before highlighting components, letting the AI overlay animation play first. */
const AI_EDIT_HIGHLIGHT_DELAY_MS = 2250;

interface EmailMakerAiEditState {
  isEnabled: boolean;
  highlightTimeoutId: ReturnType<typeof setTimeout> | undefined;
}

export interface AiRewriteEmailCampaignTextOptions {
  prompt: string;
  selectedPromptOptionValues: SelectedPromptOptionValues;
  usageSource: EmailCampaignAiRewriteSource;
  contentKey: string;
}

export interface AiRegenerateEmailCampaignImagesOptions {
  prompt: string;
  selectedPromptOptionValues: SelectedPromptOptionValues;
  usageSource: EmailCampaignAiRegenerateImageSource;
  contentKey: string;
}

export interface AiRegenerateEmailTemplateDesignsOptions {
  prompt: string;
  selectedValues: SelectedValueType;
}

export interface AiRedesignEmailCampaignOptions {
  prompt: string;
  selectedPromptOptionValues: SelectedPromptOptionValues;
  editableComponentsMap: EditableComponentsMap;
  usageSource: EmailCampaignAiRedesignSource;
}

export interface ApplyAutoBrandingStyleOptions {
  colorPalette: ColorPaletteInfo;
  html: string;
  onSuccess?: (editor: Editor, appliedColorPalette: ColorPaletteInfo) => void;
  updateColorPalette?: boolean;
  onUpdatedColorPalette?: (appliedColorPalette: ColorPaletteInfo, suggestedColorPalettes: ColorPaletteInfo[]) => void;
}

let colorPalettesCache: ColorPaletteInfo[] = [];
let colorPalettePillsCache: ColorPalettePill[] = [];

/** Single sanctioned widening point for callers that send only a subset of prompt options. */
export function toSelectedPromptOptionValues(options: Partial<SelectedPromptOptionValues>): SelectedPromptOptionValues {
  return options as SelectedPromptOptionValues;
}

export {fetchAiRewriteRecentPrompt};

/** AI-rewrites all text components and refreshes team credits. */
export async function aiRewriteEmailCampaignTextComponents(editor: Editor, options: AiRewriteEmailCampaignTextOptions): Promise<void> {
  const {prompt, selectedPromptOptionValues, usageSource, contentKey} = options;
  const componentsData: TextComponentData[] = getEmailTextWithCcid(editor, usageSource);

  const updatedComponentsData = (await window.PMW.writeLocal('emailcampaignaicontent/rewriteEmailCampaignTextComponentsContent', {
    componentsData,
    selectedPromptOptionValues,
    prompt,
    usageSource,
    contentKey,
  })) as TextComponentData[];

  updatedComponentsData.forEach((componentData) => {
    const updatedComponent = editor.DomComponents.getWrapper()?.find(`#${componentData.ccid}`)[0];
    updatedComponent?.components(componentData.content);
  });

  await refreshTeamCreditsInStore();
}

/** Regenerates every image/background/design with per-component loading overlays, settles the credit charge even on failure, and resolves with the total cost charged. */
export async function aiRegenerateEmailCampaignImages(editor: Editor, options: AiRegenerateEmailCampaignImagesOptions): Promise<number> {
  const {prompt, selectedPromptOptionValues, usageSource, contentKey} = options;
  const {imageComponentDataList, allCcids, errors, allComponents} = await gatherImagesData(editor);
  const emailContext = getEmailContextForImageGeneration(editor);
  let totalCostCharged: number;

  allComponents.forEach((component) => {
    toggleComponentLoadingOverlay(editor, component, true);
  });

  try {
    await initiateImageRegenerationWithOverlayCleanup(editor, allComponents, {
      prompt,
      imageComponentDataList,
      emailContext,
      selectedPromptOptionValues,
      usageSource,
      contentKey,
    });

    const pollResult = await pollAndReplaceGeneratedImages(editor, allCcids, contentKey);
    Object.assign(errors, pollResult.generationErrors);

    if (Object.keys(errors).length > 0 || pollResult.unresolvedCcids.length > 0) {
      clearComponentOverlaysByCcid(editor, [...Object.keys(errors), ...pollResult.unresolvedCcids]);
      throw new AiTextPromptBoxError(`Failed to generate some images`, AiTextErrorTypes.ImageGenerationError);
    }
  } finally {
    totalCostCharged = await settleImageRegenerationSafely(contentKey, prompt, selectedPromptOptionValues, usageSource);
  }

  return totalCostCharged;
}

/** Template-wide image regeneration with fixed defaults (formal mood, Flux model). */
export async function aiRegenerateEmailTemplateImages(editor: Editor, prompt: string): Promise<number> {
  return aiRegenerateEmailCampaignImages(editor, {
    prompt,
    selectedPromptOptionValues: toSelectedPromptOptionValues({
      [PromptOption.MOOD]: RedesignMood.Formal,
      [PromptOption.AI_MODEL]: AiModel.Flux,
    }),
    usageSource: EmailCampaignAiRegenerateImageSource.EMAIL_MAKER_AI_REDESIGN,
    contentKey: getUniqueId(),
  });
}

/** Morphs every PMW design component through the AI prompt, in parallel. */
export async function aiRegenerateEmailTemplateDesigns(editor: Editor, options: AiRegenerateEmailTemplateDesignsOptions): Promise<void> {
  const designComponents = editor.getWrapper()?.find(`[data-pmw-type="${EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_DESIGN}"]`);
  if (!designComponents) {
    return;
  }

  await Promise.all(designComponents.map((designComponent) => morphDesign(editor, designComponent, options.prompt, options.selectedValues)));
}

/** Full AI redesign flow (rewrite + image regeneration) with toast/Redux loading state and the feedback prompt. */
export async function aiRedesignEmailCampaign(editor: Editor, options: AiRedesignEmailCampaignOptions): Promise<void> {
  const {prompt, selectedPromptOptionValues, editableComponentsMap, usageSource} = options;
  const contentKey = getUniqueId();
  let error: ErrorType | undefined = undefined;
  let totalCharged = 0;

  try {
    initiatePosterMorphLoading(EMAIL_MAKER_AI_REDESIGN_LOADING_TOAST, 'pmwjs_redesigning_with_ai_ellipsis');
    window.PMW.redux.store.dispatch(setIsLoading(true));
    window.PMW.redux.store.dispatch(setLastRunCreditsCharged(undefined));

    totalCharged = await applyAiRedesignChanges(editor, editableComponentsMap, prompt, selectedPromptOptionValues, contentKey);
    window.PMW.redux.store.dispatch(setLastRunCreditsCharged(totalCharged));
  } catch (e) {
    error = e as ErrorType;
    handleAiRedesignError(error);
  } finally {
    const redesignHashedId = await saveAiRedesignGenerationResults(prompt, usageSource, contentKey);

    window.PMW.redux.store.dispatch(setIsLoading(false));
    terminatePosterMorphLoading(EMAIL_MAKER_AI_REDESIGN_LOADING_TOAST);

    if (redesignHashedId && !error) {
      const creditUsage = isMobileWidth() ? {creditsCharged: totalCharged, remainingCredits: (await getTeamCredits()).credits} : undefined;
      handleAiRedesignFeedback(redesignHashedId, creditUsage);
    }
  }
}

/** Toggles the AI-edit highlight mode on text/button/image components. */
export function toggleAiEdit(editor: Editor, enable: boolean): void {
  const editableComponentTypes: string[] = [EMAIL_MAKER_COMPONENT_TYPES.text.PMW_TEXT, EMAIL_MAKER_COMPONENT_TYPES.text.PMW_BUTTON, EMAIL_MAKER_COMPONENT_TYPES.graphic.PMW_IMAGE];
  const allImageAndTextComponents = getAllComponentsByType(editor, editableComponentTypes);

  const prevState = editor.getModel().get(AI_EDIT_STATE_MODEL_KEY) as EmailMakerAiEditState | undefined;
  if (prevState?.highlightTimeoutId !== undefined) {
    clearTimeout(prevState.highlightTimeoutId);
  }

  let highlightTimeoutId: ReturnType<typeof setTimeout> | undefined;

  if (enable) {
    toggleLoadingOverlayForEmail(editor, true, EmailOverlayStyle.AI_EDIT);
    unselectComponent(editor);
    editor.Canvas.getFrameEl().contentDocument?.querySelector(GJS_DASHED_SELECTOR)?.classList.add(AI_EDIT_FRAME_CLASS);

    highlightTimeoutId = setTimeout(() => {
      // Bail unless the editor still holds this same timeout, guarding against a destroyed/re-initialized editor.
      const currentState = editor.getModel().get(AI_EDIT_STATE_MODEL_KEY) as EmailMakerAiEditState | undefined;
      if (currentState?.highlightTimeoutId !== highlightTimeoutId) {
        return;
      }

      if (isAiEditEnabled(editor)) {
        editor.getModel().skip(() => {
          allImageAndTextComponents.forEach((component) => {
            component.addClass(AI_EDIT_HIGHLIGHT_CLASS);
          });
        });
      }

      toggleLoadingOverlayForEmail(editor, false);
    }, AI_EDIT_HIGHLIGHT_DELAY_MS);
  } else {
    editor.Canvas.getFrameEl().contentDocument?.querySelector(GJS_DASHED_SELECTOR)?.classList.remove(AI_EDIT_FRAME_CLASS);
    editor.getModel().skip(() => {
      allImageAndTextComponents.forEach((component) => {
        component.removeClass(AI_EDIT_HIGHLIGHT_CLASS);
      });
    });
  }

  const state: EmailMakerAiEditState = {isEnabled: enable, highlightTimeoutId};
  editor.getModel().set(AI_EDIT_STATE_MODEL_KEY, state);
}

/** Whether AI-edit mode is currently on for this editor. */
export function isAiEditEnabled(editor: Editor): boolean {
  const state = editor.getModel().get(AI_EDIT_STATE_MODEL_KEY) as EmailMakerAiEditState | undefined;
  return state?.isEnabled ?? false;
}

/** Applies a color palette to the email HTML server-side, then swaps the recolored HTML in behind the full-canvas overlay. */
export async function applyAutoBrandingStyle(editor: Editor, options: ApplyAutoBrandingStyleOptions): Promise<void> {
  const {colors} = options.colorPalette;
  const updateAndSuggest = options.updateColorPalette ?? false;

  toggleLoadingOverlayForEmail(editor, true);

  const startTime = Date.now();

  const response = (await window.PMW.writeLocal('autobrandingemailmaker/applyColorMappingToEmail', {
    colors,
    html: options.html,
    updateAndSuggest,
  })) as ApplyColorPaletteToEmailServerResponse;

  if (response.colors.length === 0 || !response.html) {
    toggleLoadingOverlayForEmail(editor, false);
    return;
  }

  const waitDuration = Math.max(EMAIL_OVERLAY_ANIMATION_TIME_MS - (Date.now() - startTime), 0);
  setTimeout(() => {
    applyAutoBrandingResponse(editor, options, response);
  }, waitDuration);
}

/** Prefetches the admin color palettes into the module cache. */
export function fetchAllColorPalettes(): void {
  void window.PMW.readLocal('colorpalettes/getAdminColorPalettes', {})
    .then((response: ColorPaletteInfo[]) => {
      colorPalettesCache = response;
    })
    .catch((error: unknown) => {
      logEmailMakerError('fetchAllColorPalettes: prefetch failed', error);
    });
}

/** Prefetches the palette pills into the module cache. */
export function fetchAllColorPalettePills(): void {
  void window.PMW.readLocal('colorpalettes/getColorPalettePillsToShow', {})
    .then((response: ColorPalettePill[]) => {
      colorPalettePillsCache = response;
    })
    .catch((error: unknown) => {
      logEmailMakerError('fetchAllColorPalettePills: prefetch failed', error);
    });
}

export function getColorPalettes(): ColorPaletteInfo[] {
  return colorPalettesCache;
}

export function getColorPalettePills(): ColorPalettePill[] {
  return colorPalettePillsCache;
}

interface ImageRegenerationArgs {
  prompt: string;
  imageComponentDataList: ImageComponentData[];
  emailContext: string;
  selectedPromptOptionValues: SelectedPromptOptionValues;
  usageSource: EmailCampaignAiRegenerateImageSource;
  contentKey: string;
}

/** Clears every component overlay before rethrowing a kickoff failure. */
async function initiateImageRegenerationWithOverlayCleanup(editor: Editor, allComponents: Component[], args: ImageRegenerationArgs): Promise<void> {
  try {
    await initiateAiRedesignImageRegeneration(args);
  } catch (err) {
    allComponents.forEach((component) => {
      toggleComponentLoadingOverlay(editor, component, false);
    });
    throw err;
  }
}

/** Credit settle + team-credits refresh; errors must not mask the generation error. */
async function settleImageRegenerationSafely(
  contentKey: string,
  prompt: string,
  selectedPromptOptionValues: SelectedPromptOptionValues,
  usageSource: EmailCampaignAiRegenerateImageSource
): Promise<number> {
  let cost = 0;
  try {
    cost = await settleEmailCampaignAiRedesignImageRegeneration(contentKey, prompt, selectedPromptOptionValues, usageSource);
    await refreshTeamCreditsInStore();
  } catch (settleErr) {
    window.PMW.log('AI redesign settle failed', settleErr);
  }
  return cost;
}

function clearComponentOverlaysByCcid(editor: Editor, ccids: string[]): void {
  ccids.forEach((ccid) => {
    const component = editor.getWrapper()?.find(`#${ccid}`)[0];
    if (component) {
      toggleComponentLoadingOverlay(editor, component, false);
    }
  });
}

/** Regenerates one PMW design component and reloads its preview. */
async function morphDesign(editor: Editor, designComponent: Component, prompt: string, configOptions: SelectedValueType): Promise<void> {
  try {
    setImageLoading(editor, {component: designComponent, loading: true});

    const designHashedId = getStringProp(designComponent, EMAIL_MAKER_PROPS.DESIGN_ID) ?? '';
    const designPageId = window.PMW.enableMyStuffMultipagesUI ? (getStringProp(designComponent, EMAIL_MAKER_PROPS.DESIGN_PAGE_ID) ?? '') : null;

    const response = (await window.PMW.readLocal('emailcampaignaicontent/morphDesignPoster', {
      designHashedId,
      designPageId,
      prompt,
      configOptions,
    })) as PosterPageHashedIdInfo;

    if (window.PMW.enableMyStuffMultipagesUI) {
      void loadDesignPageForId(editor, designComponent, response);
    } else {
      void loadDesignForId(editor, designComponent, response.posterHashedId);
    }
  } catch (e: unknown) {
    logEmailMakerError('morphDesign: failed to morph design component', e);
    setImageLoading(editor, {component: designComponent, loading: false});
  }
}

/** Swaps the recolored HTML in and reports the applied/suggested palettes. */
function applyAutoBrandingResponse(editor: Editor, options: ApplyAutoBrandingStyleOptions, response: ApplyColorPaletteToEmailServerResponse): void {
  const {id, colors} = options.colorPalette;
  const updateAndSuggest = options.updateColorPalette ?? false;

  replaceHtmlContent(editor, response.html);

  if (id && updateAndSuggest && response.suggested && options.onUpdatedColorPalette) {
    const appliedPalette: ColorPaletteInfo = {
      id,
      colors: response.colors,
      type: ColorPaletteType.BrandKit,
      name: colors.length === response.colors.length ? options.colorPalette.name : window.i18next.t('pmwjs_suggested'),
    };
    const suggestedPalettes: ColorPaletteInfo[] = response.suggested.map((colorPalette): ColorPaletteInfo => {
      return {
        id,
        colors: colorPalette,
        type: ColorPaletteType.BrandKit,
        name: window.i18next.t('pmwjs_suggested'),
      };
    });

    options.onUpdatedColorPalette(appliedPalette, suggestedPalettes);
  } else {
    options.onSuccess?.(editor, options.colorPalette);
  }

  toggleLoadingOverlayForEmail(editor, false);
}
